import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import { DASH } from '@/globals';
import formatNumber from '@/utils/NumberFormatter';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

import ReportDefinition, { IReportDefinitionAPI } from './ReportDefinition';

interface IMeta {
  totals?: Record<string, number>;
}

interface IReportExecutionAPI {
  report_execution_id: number;
  report_definition_id: number;
  report_definition: IReportDefinitionAPI;
  execution_started_time_utc: string;
  execution_finished_time_utc: string;
  report_file_url: string;
  status_code: string;
  status_message: string;
  progress: number;
  meta: IMeta;
}

interface IReportExecution {
  id: number;
  reportDefinitionId: number;
  reportDefinition: ReportDefinition;
  executionStartedTimeUtc: DateTime;
  executionFinishedTimeUtc: DateTime;
  reportFileUrl: string;
  statusCode: string;
  statusMessage: string;
  progress: number;
}

export default class ReportExecution extends Validatable implements IReportExecution {
  reportDefinitionId: number;
  reportDefinition: ReportDefinition;
  executionStartedTimeUtc: DateTime;
  executionFinishedTimeUtc: DateTime;
  reportFileUrl: string;
  statusCode: string;
  statusMessage: string;
  progress: number;
  meta: IMeta;

  constructor(
    id = -1,
    reportDefinitionId = null,
    reportDefinition = null,
    executionStartedTimeUtc = null,
    executionFinishedTimeUtc = null,
    reportFileUrl = null,
    statusCode = null,
    statusMessage = null,
    progress = null,
    meta = {},
  ) {
    super();
    this.id = id;
    this.reportDefinitionId = reportDefinitionId;
    this.reportDefinition = reportDefinition;
    this.executionStartedTimeUtc = executionStartedTimeUtc;
    this.executionFinishedTimeUtc = executionFinishedTimeUtc;
    this.reportFileUrl = reportFileUrl;
    this.statusCode = statusCode;
    this.statusMessage = statusMessage;
    this.progress = progress;
    this.meta = meta;
  }

  get status() {
    switch (this.statusCode) {
      case 'ERROR':
        return capitalize(i18n.global.t('names.error', { n: 1 }));
      case 'DONE':
        return capitalize(i18n.global.t('states.done'));
      case 'QUEUD':
      case 'QUEUED':
        return capitalize(i18n.global.t('states.queued'));
      case 'RUNNING':
        switch (this.statusMessage) {
          case 'PROCESSING_PLAYLOGS':
            return capitalize(i18n.global.t('states.processingPlaylogs'));
          case 'ASSEMBLING_REPORT':
            return capitalize(i18n.global.t('states.assemblingReport'));
          default:
            return `${capitalize(i18n.global.t('states.running'))} (${this.progressFormatted})`;
        }
      case 'WAIT_AMEND':
        return capitalize(i18n.global.t('states.waitAmend'));
      case 'DONE_AMEND':
        return capitalize(i18n.global.t('states.doneAmend'));
      case 'FAIL_AMEND':
        return capitalize(i18n.global.t('states.failAmend'));
      default:
        return capitalize(i18n.global.t('states.unknown'));
    }
  }

  get isRunning() {
    return (
      this.statusCode !== 'DONE' &&
      this.statusCode !== 'ERROR' &&
      this.statusCode !== 'WAIT_AMEND' &&
      this.statusCode !== 'DONE_AMEND' &&
      this.statusCode !== 'FAIL_AMEND'
    );
  }

  get isSuccess() {
    return (
      this.statusCode === 'DONE' ||
      this.statusCode === 'WAIT_AMEND' ||
      this.statusCode === 'DONE_AMEND' ||
      this.statusCode === 'FAIL_AMEND'
    );
  }

  get isError() {
    return this.statusCode === 'ERROR';
  }

  get startedOnFormatted() {
    return this.executionStartedTimeUtc.toLocal().toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
  }

  get finishedOnFormatted() {
    if (this.executionFinishedTimeUtc !== null) {
      return this.executionFinishedTimeUtc.toLocal().toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    }
    return DASH;
  }

  get durationInSeconds() {
    if (this.executionFinishedTimeUtc !== null) {
      let { seconds } = this.executionFinishedTimeUtc.diff(this.executionStartedTimeUtc, 'seconds').toObject();
      if (seconds === 0) {
        seconds = 1;
      }
      return seconds;
    }
    return null;
  }

  get durationInSecondsFormatted() {
    if (this.durationInSeconds !== null) {
      return `${this.durationInSeconds} ${i18n.global.t('names.second', Math.floor(this.durationInSeconds))}`;
    }
    return DASH;
  }

  get progressFormatted() {
    return formatNumber(this.progress, '0%');
  }

  static getStatusCodes() {
    return [
      {
        id: 'ERROR',
        name: capitalize(i18n.global.t('names.error', { n: 1 })),
      },
      {
        id: 'DONE',
        name: capitalize(i18n.global.t('states.done')),
      },
      {
        id: 'QUEUED',
        name: capitalize(i18n.global.t('states.queued')),
      },
      {
        id: 'RUNNING',
        name: capitalize(i18n.global.t('states.running')),
      },
      {
        id: 'WAIT_AMEND',
        name: capitalize(i18n.global.t('states.waitAmend')),
      },
      {
        id: 'DONE_AMEND',
        name: capitalize(i18n.global.t('states.doneAmend')),
      },
      {
        id: 'FAIL_AMEND',
        name: capitalize(i18n.global.t('states.failAmend')),
      },
    ];
  }

  static fromApi(apiObject: IReportExecutionAPI) {
    return new ReportExecution(
      apiObject.report_execution_id,
      apiObject.report_definition_id,
      apiObject.report_definition != null ? ReportDefinition.fromApi(apiObject.report_definition) : null,
      DateTime.fromISO(apiObject.execution_started_time_utc, { zone: 'utc' }),
      apiObject.execution_finished_time_utc !== null && apiObject.execution_finished_time_utc !== undefined
        ? DateTime.fromISO(apiObject.execution_finished_time_utc, { zone: 'utc' })
        : null,
      apiObject.report_file_url,
      apiObject.status_code,
      apiObject.status_message,
      apiObject.progress,
      apiObject.meta,
    );
  }

  static fromObject(obj: IReportExecution) {
    return super.fromObject(obj, new ReportExecution());
  }

  toApi() {
    return {
      report_execution_id: this.id >= 0 ? this.id : undefined,
      report_definition_id: this.reportDefinitionId,
      execution_started_time_utc: this.executionStartedTimeUtc,
      execution_finished_time_utc: this.executionFinishedTimeUtc,
      report_file_url: this.reportFileUrl,
      status_code: this.statusCode,
      status_message: this.statusMessage,
      progress: this.progress,
    };
  }
}
